import type { AssetMetricPoint } from '@src/providers/CoinMetricsApiProvider';
import { useCoinMetricsApiKey } from '@src/providers/CoinMetricsApiProvider';
import { useEffect, useMemo, useState } from 'react';
import type { AssetLeaderboardRow } from './AssetLeaderboard.types';
import { computePercentChange, parseMetricValue } from './assetLeaderboardMetrics';
import { useGetAssetLeaderboardSnapshotQuery } from './assetLeaderboardSnapshotApi';
import { useAssetLeaderboardSnapshotProgress } from './assetLeaderboardSnapshotProgress';
import {
  ASSET_LEADERBOARD_24H_OFFSET_HOURS,
  ASSET_LEADERBOARD_7D_OFFSET_HOURS,
  ASSET_LEADERBOARD_DAILY_CHANGE_TOLERANCE_HOURS,
  ASSET_LEADERBOARD_FREQUENCY,
  ASSET_LEADERBOARD_FRESHNESS_WINDOW_DAYS,
  ASSET_LEADERBOARD_LOOKBACK_DAYS,
  ASSET_LEADERBOARD_MARKET_CAP_METRIC,
  ASSET_LEADERBOARD_METRICS,
  ASSET_LEADERBOARD_POLL_MS,
  ASSET_LEADERBOARD_PRICE_METRIC,
  ASSET_LEADERBOARD_VOLUME_METRIC,
} from './tokens';
import { useAssetLeaderboardOneHourChange } from './useAssetLeaderboardOneHourChange';
import { useAssetLeaderboardUniverse } from './useAssetLeaderboardUniverse';

interface UseAssetLeaderboardDataResult {
  /** One row per asset in the resolved universe. */
  rows: AssetLeaderboardRow[];
  /** Size of the resolved universe currently being fetched — 0 until the universe resolves. Surfaced so
   * the initial-load UI can show how many assets it's waiting on (see `isLoading`). */
  assetCount: number;
  /** Assets whose snapshot batch has settled this cycle (out of `assetCount`) — drives the initial-load
   * progress bar. Resets to 0 at the start of each fetch cycle. */
  loadedAssetCount: number;
  isLoading: boolean;
  /** True only when there's no usable data at all — nothing to fall back to (see AssetLeaderboardSnapshot). */
  isError: boolean;
  /**
   * Required metrics the org's API key appears to have no access to at all (see
   * `detectUnentitledLeaderboardMetrics`) — empty once the key has full access, or before the
   * snapshot has resolved.
   */
  missingMetrics: string[];
}

const EMPTY_ROWS: AssetLeaderboardRow[] = [];
const EMPTY_ONE_HOUR_CHANGE: ReadonlyMap<string, number> = new Map();

/**
 * The most recent non-null value of `metric` across `points` (assumed sorted oldest-to-newest), or
 * null when no point carries it.
 *
 * A given daily row only contains the metrics that had a value at that timestamp, and CoinMetrics
 * publishes the requested metrics on different lags — `ReferenceRateUSD` lands sooner than
 * `CapMrktEstUSD`/`volume_reported_spot_usd_1d`, so the newest row often carries price alone while
 * market cap and volume sit on the row a day earlier. Reading each metric off the single newest row
 * (`points.at(-1)`) would therefore surface null market cap/volume for nearly every asset even though
 * the values are present a row back; scanning back to each metric's own latest value avoids that.
 */
function latestMetricValue(points: AssetMetricPoint[], metric: string): number | null {
  for (let i = points.length - 1; i >= 0; i--) {
    const value = parseMetricValue(points[i][metric]);
    if (value != null) {
      return value;
    }
  }
  return null;
}

const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * Price of the daily point closest to `offsetMs` before `latestTimeMs`, but only when that point is
 * within `toleranceMs` of the target — otherwise null.
 *
 * Selecting the comparison point by timestamp (rather than by array position like `at(-2)`/`[0]`) is
 * what makes the 24H/7D change honest when the daily series has gaps: CoinMetrics returns the N most
 * recent *available* points (`paging_from: 'end'`), so a thinly-traded asset that skipped a day would
 * otherwise have a 48h move reported as "24H" or a >7d move reported as "7D". A missing observation at
 * the target day pushes the nearest point a full day past the tolerance, yielding a null change rather
 * than a mislabeled one. Points with a null price are skipped. `points` are assumed sorted oldest→newest.
 */
function priceAtOffset(
  points: AssetMetricPoint[],
  latestTimeMs: number,
  offsetMs: number,
  toleranceMs: number
): number | null {
  const targetMs = latestTimeMs - offsetMs;
  let best: { price: number; distanceMs: number } | null = null;
  for (const point of points) {
    const price = parseMetricValue(point[ASSET_LEADERBOARD_PRICE_METRIC]);
    if (price == null) {
      continue;
    }
    const timeMs = new Date(point.time).getTime();
    if (!Number.isFinite(timeMs)) {
      continue;
    }
    const distanceMs = Math.abs(timeMs - targetMs);
    if (distanceMs > toleranceMs) {
      continue;
    }
    if (best == null || distanceMs < best.distanceMs) {
      best = { price, distanceMs };
    }
  }
  return best?.price ?? null;
}

/**
 * True when `time` (an ISO timestamp) is no older than `freshnessWindowDays` relative to `now`.
 *
 * This is a safety net for a gap the catalog-based eligibility check (`useAssetLeaderboardUniverse`) can't
 * fully close on its own: that check only re-runs periodically (on the same cadence as this hourly
 * refresh), not on every render, so an asset could still be "eligible" from a check done up to one
 * poll cycle ago. If that asset has since stopped reporting data, CoinMetrics' `paging_from: 'end'`
 * still returns its last-ever point — however old — so a "successful" fetch alone doesn't guarantee
 * fresh data. Checking the actual returned point's own timestamp on every refresh catches that case
 * regardless of whether eligibility has caught up yet. Exported for unit testing.
 */
export function isPointFresh(time: string, now: Date, freshnessWindowDays: number): boolean {
  const pointTimeMs = new Date(time).getTime();
  const cutoffMs = now.getTime() - freshnessWindowDays * 24 * 60 * 60 * 1000;
  return Number.isFinite(pointTimeMs) && pointTimeMs >= cutoffMs;
}

/**
 * `requiredMetrics` for which none of `points` carries a non-null value — i.e. the org's CoinMetrics
 * API key can fetch *some* of the requested metrics but appears to have no access to at least one of
 * them. CoinMetrics doesn't reject a multi-metric request outright when only some of the requested
 * metrics are unentitled for the key — unlike a wholly invalid key (401) or a fully unentitled metric
 * on its own (403), it silently omits the unentitled ones from every returned point instead of
 * erroring, so this has to be detected empirically from the response rather than from a request
 * error. Only meaningful once `points` actually has data — an empty/undefined snapshot is already
 * reported as "loading" or "no data at all" by `deriveAssetLeaderboardLoadState`. Exported for unit
 * testing.
 */
export function detectUnentitledLeaderboardMetrics(
  points: AssetMetricPoint[] | undefined,
  requiredMetrics: readonly string[]
): string[] {
  if (points == null || points.length === 0) {
    return [];
  }
  return requiredMetrics.filter(metric => points.every(point => parseMetricValue(point[metric]) == null));
}

/**
 * Builds one row per asset from its points in the lookback window (oldest to newest once sorted).
 * 24h/7d change compare the latest price against the daily point nearest that many hours back, within
 * `ASSET_LEADERBOARD_DAILY_CHANGE_TOLERANCE_HOURS` (see `priceAtOffset`) — so a gap in the daily series
 * (a skipped day, a recent listing, or a truncated fetch) yields a null change rather than silently
 * comparing across a wider span and mislabeling it.
 * An asset with no data at all for any of the three metrics, or whose newest point is itself stale
 * (see `isPointFresh`), is dropped entirely rather than shown as a blank or misleadingly-current row —
 * CoinMetrics' Talos-symbol mapping covers plenty of ids (leveraged/wrapper tokens, delisted pairs) it
 * has no real price/market-cap/volume data for, and those aren't meaningful rows on an asset screen
 * (unlike e.g. Funding Rate Blotter, where a blank row is a user-configured asset temporarily missing a
 * snapshot). Exported for unit testing.
 */
export function buildAssetLeaderboardRows({
  cmAssetIds,
  symbolByCmAssetId,
  points,
  now,
  freshnessWindowDays,
  oneHourChangeByCmAssetId = EMPTY_ONE_HOUR_CHANGE,
}: {
  cmAssetIds: string[];
  symbolByCmAssetId: ReadonlyMap<string, string>;
  points: AssetMetricPoint[] | undefined;
  now: Date;
  freshnessWindowDays: number;
  /** 1H change per asset, from the separate hourly fetch — merged onto the daily-built row. */
  oneHourChangeByCmAssetId?: ReadonlyMap<string, number>;
}): AssetLeaderboardRow[] {
  if (cmAssetIds.length === 0) {
    return EMPTY_ROWS;
  }

  const pointsByAsset = new Map<string, AssetMetricPoint[]>();
  for (const point of points ?? []) {
    const existing = pointsByAsset.get(point.asset);
    if (existing) {
      existing.push(point);
    } else {
      pointsByAsset.set(point.asset, [point]);
    }
  }
  for (const [assetId, assetPoints] of pointsByAsset) {
    pointsByAsset.set(
      assetId,
      assetPoints.toSorted((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
    );
  }

  const rows: AssetLeaderboardRow[] = [];
  for (const cmAssetId of cmAssetIds) {
    const symbol = symbolByCmAssetId.get(cmAssetId) ?? cmAssetId.toUpperCase();
    const assetPoints = pointsByAsset.get(cmAssetId);
    const sortedPoints = assetPoints ?? [];
    const latest = assetPoints?.at(-1);
    if (latest && !isPointFresh(latest.time, now, freshnessWindowDays)) {
      continue;
    }

    const price = parseMetricValue(latest?.[ASSET_LEADERBOARD_PRICE_METRIC]);
    const spotVolumeUsd = latestMetricValue(sortedPoints, ASSET_LEADERBOARD_VOLUME_METRIC);
    const marketCapUsd = latestMetricValue(sortedPoints, ASSET_LEADERBOARD_MARKET_CAP_METRIC);
    if (price == null && spotVolumeUsd == null && marketCapUsd == null) {
      continue;
    }

    // 24H/7D compare the latest price against the daily point nearest that many hours back (within
    // tolerance), so a gap in the daily series yields a null change rather than a mislabeled one.
    const latestTimeMs = latest ? new Date(latest.time).getTime() : NaN;
    const hasLatestTime = Number.isFinite(latestTimeMs);
    const toleranceMs = ASSET_LEADERBOARD_DAILY_CHANGE_TOLERANCE_HOURS * MS_PER_HOUR;
    const change24hPct =
      price != null && hasLatestTime
        ? computePercentChange(
            price,
            priceAtOffset(sortedPoints, latestTimeMs, ASSET_LEADERBOARD_24H_OFFSET_HOURS * MS_PER_HOUR, toleranceMs)
          )
        : null;
    const change7dPct =
      price != null && hasLatestTime
        ? computePercentChange(
            price,
            priceAtOffset(sortedPoints, latestTimeMs, ASSET_LEADERBOARD_7D_OFFSET_HOURS * MS_PER_HOUR, toleranceMs)
          )
        : null;

    const priceHistory = sortedPoints
      .map(point => parseMetricValue(point[ASSET_LEADERBOARD_PRICE_METRIC]))
      .filter((point): point is number => point != null);

    rows.push({
      id: cmAssetId,
      symbol,
      cmAssetId,
      price,
      change1hPct: oneHourChangeByCmAssetId.get(cmAssetId) ?? null,
      change24hPct,
      change7dPct,
      spotVolumeUsd,
      marketCapUsd,
      priceHistory,
    });
  }
  return rows;
}

/**
 * Derives loading/error state from the universe and the shared snapshot query's result. `points` must be
 * RTK Query's `currentData` (not `data`) — `data` and the `isLoading`/`isUninitialized` flags all fall
 * back to the *previous* cache key's result while a new key's first fetch is in flight (RTK Query does
 * this deliberately, so ordinary arg changes don't flash a loader), which would otherwise make a genuine
 * universe change look like "loaded, zero points" — i.e. a hard error — instead of "still loading".
 * `currentData` has no such fallback, so `points !== undefined` is the one check that actually tracks
 * "has *this* cache key resolved at least once".
 *
 * `hasAssetsToFetch` must mirror the same condition the caller uses to `skip` the snapshot query
 * (`cmAssetIds.length > 0`). When there's nothing to fetch, the query is deliberately skipped and its
 * `currentData` never resolves, so `points !== undefined` can never become true — without this escape
 * hatch, a universe that legitimately resolves to zero eligible assets would report `isLoading: true`
 * forever instead of an empty, non-error leaderboard. Exported for unit testing.
 */
export function deriveAssetLeaderboardLoadState({
  isUniverseLoading,
  isUniverseError,
  hasAssetsToFetch,
  points,
}: {
  isUniverseLoading: boolean;
  isUniverseError: boolean;
  hasAssetsToFetch: boolean;
  points: AssetMetricPoint[] | undefined;
}): { isLoading: boolean; isError: boolean } {
  if (!hasAssetsToFetch) {
    return { isLoading: isUniverseLoading, isError: isUniverseError };
  }

  const hasLoadedSnapshot = points !== undefined;
  return {
    isLoading: isUniverseLoading || !hasLoadedSnapshot,
    // A hard error only when there's nothing at all to show. A cycle where every batch failed but left
    // carried-over data from a previous one keeps showing rows instead of replacing them with an error
    // screen — see `assetLeaderboardSnapshotApi`'s carry-over merge.
    isError: isUniverseError || (hasLoadedSnapshot && points.length === 0),
  };
}

/**
 * Resolves the full CoinMetrics asset universe (intersected with Talos-known symbols, via
 * `useAssetLeaderboardUniverse`) and reads the shared, cross-panel snapshot fetched in bounded-
 * concurrency batches by `assetLeaderboardSnapshotApi`, then builds the Asset Leaderboard rows
 * (dropping any asset with no data for our metrics). This is the REST snapshot layer, polled hourly;
 * the live `ReferenceRateUSD` price is overlaid downstream on visible rows via the websocket enrichment
 * (`useAssetLeaderboardPriceEnrichment`). Only the absolute 1d/1w/1m/3m/1y time-window control remains
 * a later phase.
 */
export function useAssetLeaderboardData(): UseAssetLeaderboardDataResult {
  const apiKey = useCoinMetricsApiKey();

  const {
    talosSymbolByCmAssetId,
    cmAssetIdsWith1hPrice,
    isLoading: isUniverseLoading,
    isError: isUniverseError,
    missingMetrics: universeMissingMetrics,
  } = useAssetLeaderboardUniverse();

  // 1H change comes from a separate hourly fetch, scoped to the 1h-capable subset of the universe, and
  // is merged onto each daily-built row below. Independent of the daily snapshot cycle.
  const oneHourChangeByCmAssetId = useAssetLeaderboardOneHourChange(cmAssetIdsWith1hPrice);

  // The universe hook returns a fresh map identity every hourly `now` tick even when membership is
  // unchanged. Deriving cmAssetIds from a sorted content signature keeps it referentially stable across
  // those ticks (an unchanged universe shouldn't re-trigger a fetch), and canonical regardless of the
  // reference-data response's own ordering — this signature is also the shared snapshot's cache key
  // (`AssetLeaderboardSnapshotArgs.signature`), so two panels resolving the same universe in a
  // different id order still land on the same cache entry and share one fetch/poll cycle rather than
  // silently doubling requests.
  const cmAssetIdsSignature = useMemo(
    () => Array.from(talosSymbolByCmAssetId.keys()).toSorted().join(','),
    [talosSymbolByCmAssetId]
  );
  const cmAssetIds = useMemo(
    () => (cmAssetIdsSignature === '' ? [] : cmAssetIdsSignature.split(',')),
    [cmAssetIdsSignature]
  );
  const metricsParam = useMemo(() => ASSET_LEADERBOARD_METRICS.join(','), []);

  // `currentData` (not `data`) so a genuine universe change — a new cache key — shows the loading state
  // until that key's own first fetch resolves, rather than briefly rendering the previous universe's
  // stale rows under the new asset list — see `deriveAssetLeaderboardLoadState`'s doc for why `currentData`
  // specifically.
  const { currentData: points } = useGetAssetLeaderboardSnapshotQuery(
    {
      signature: cmAssetIdsSignature,
      metrics: metricsParam,
      frequency: ASSET_LEADERBOARD_FREQUENCY,
      limitPerAsset: ASSET_LEADERBOARD_LOOKBACK_DAYS,
    },
    { skip: cmAssetIds.length === 0 || !apiKey, pollingInterval: ASSET_LEADERBOARD_POLL_MS }
  );
  const { loadedAssetCount } = useAssetLeaderboardSnapshotProgress(cmAssetIdsSignature);

  // Re-evaluates `isPointFresh` on this same cadence rather than relying on `points` getting a new
  // reference every poll: RTK Query applies structural sharing, so a poll that returns byte-identical
  // data (the common case once a day's metrics have settled) keeps the same `points` reference and would
  // never re-trigger this memo on its own.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), ASSET_LEADERBOARD_POLL_MS);
    return () => clearInterval(intervalId);
  }, []);

  // Hold rows until the universe resolves, otherwise we'd briefly render nothing for every asset before
  // we know which returned assets are Talos-known.
  const rows = useMemo(
    () =>
      isUniverseLoading
        ? EMPTY_ROWS
        : buildAssetLeaderboardRows({
            cmAssetIds,
            symbolByCmAssetId: talosSymbolByCmAssetId,
            points,
            now: new Date(now),
            freshnessWindowDays: ASSET_LEADERBOARD_FRESHNESS_WINDOW_DAYS,
            oneHourChangeByCmAssetId,
          }),
    [isUniverseLoading, cmAssetIds, talosSymbolByCmAssetId, points, oneHourChangeByCmAssetId, now]
  );

  const { isLoading, isError } = deriveAssetLeaderboardLoadState({
    isUniverseLoading,
    isUniverseError,
    hasAssetsToFetch: cmAssetIds.length > 0,
    points,
  });

  // Two independent places a metric can turn out to be unentitled: the catalog can already omit it
  // for every asset (`universeMissingMetrics` — the universe then resolves empty with no error of its
  // own), or the catalog can claim it's available while the actual timeseries pull silently omits it
  // from every point (`detectUnentitledLeaderboardMetrics`, the case that produced a small-but-real
  // universe and a "successful" fetch with nothing usable in it). Surface either as the same message.
  const snapshotMissingMetrics = useMemo(
    () => detectUnentitledLeaderboardMetrics(points, ASSET_LEADERBOARD_METRICS),
    [points]
  );
  const missingMetrics = useMemo(
    () => Array.from(new Set([...universeMissingMetrics, ...snapshotMissingMetrics])),
    [universeMissingMetrics, snapshotMissingMetrics]
  );

  return {
    rows,
    assetCount: cmAssetIds.length,
    loadedAssetCount,
    isLoading,
    isError,
    missingMetrics,
  };
}
